import { Injectable, inject } from '@angular/core';
import {
  WORKFLOW_RUN_STORAGE,
  type WorkflowRunStorageSnapshot,
} from '../services/workflow-run-storage.adapter';
import { evaluateCondition, type ConditionExpression } from './condition-evaluator';
import { DEFAULT_CATEGORY_EXECUTORS } from '../executors';
import type { CategoryExecutor, ExecutorContext } from '../executors/executor.types';
import type {
  CanonicalEdge,
  CanonicalNode,
  CanonicalWorkflowDocument,
  WorkflowPayload,
} from '../models/canonical.types';
import type {
  ExecutionTraceEntry,
  ResumeOutcome,
  RunState,
  RunStateEdge,
} from '../models/run-state.types';
import { NodeCapabilityRegistry } from '../registry/node-capability.registry';
import { BackendProxyService } from '../services/backend-proxy.service';
import { ConfigPackLoader } from '../services/config-pack.loader';
import { ExpressionEvaluator } from '../services/expression-evaluator';

const DEFAULT_MAX_STEPS = 500;
/** Legacy sessionStorage key prefix — kept for migration reference only. */
export const WF_RUN_STORAGE_PREFIX = 'wf-run:';
/** Legacy active pointer prefix — kept for migration reference only. */
export const WF_RUN_ACTIVE_POINTER_PREFIX = 'wf-run:active:';

type WorkflowGraphIndex = {
  nodeByKey: Map<string, CanonicalNode>;
  outgoingByKey: Map<string, CanonicalEdge[]>;
};

type InternalRun = {
  instanceId: string;
  workflowKey: string;
  versionNumber: number;
  definition: CanonicalWorkflowDocument;
  graph?: WorkflowGraphIndex;
  payload: WorkflowPayload;
  trace: ExecutionTraceEntry[];
  pausedNodeKey?: string;
  status: RunState['status'];
  error?: { code: string; message: string };
};

@Injectable({ providedIn: 'root' })
export class WorkflowGraphWalker {
  private readonly registry = inject(NodeCapabilityRegistry);
  private readonly proxy = inject(BackendProxyService);
  private readonly pack = inject(ConfigPackLoader);
  private readonly runStorage = inject(WORKFLOW_RUN_STORAGE);
  private readonly expressions = new ExpressionEvaluator();
  private readonly executors: Record<string, CategoryExecutor> = {
    ...DEFAULT_CATEGORY_EXECUTORS,
  };
  private readonly runs = new Map<string, InternalRun>();

  async start(
    workflowKey: string,
    input: WorkflowPayload = {},
    options?: { instanceId?: string; maxSteps?: number },
  ): Promise<RunState> {
    const definition = await this.pack.getWorkflow(workflowKey);
    const instanceId = options?.instanceId ?? crypto.randomUUID();
    const run: InternalRun = {
      instanceId,
      workflowKey: definition.workflowKey,
      versionNumber: definition.versionNumber,
      definition,
      payload: { ...input },
      trace: [],
      status: 'running',
    };
    this.runs.set(instanceId, run);

    const startNode = definition.nodes.find((n) => this.registry.isStartNode(n.nodeType));
    if (!startNode) {
      run.status = 'failed';
      run.error = { code: 'MISSING_START', message: 'Workflow has no START node' };
      return this.toRunState(run);
    }

    await this.executeFrom(run, startNode, options?.maxSteps ?? DEFAULT_MAX_STEPS);
    this.persist(run);
    return this.toRunState(run);
  }

  async resume(instanceId: string, outcome: ResumeOutcome = {}): Promise<RunState> {
    const run = await this.resolveRun(instanceId);
    if (!run) throw new Error(`Unknown run instance "${instanceId}"`);
    if (run.status !== 'paused' || !run.pausedNodeKey) {
      throw new Error(`Run "${instanceId}" is not paused`);
    }

    const { nodeByKey, outgoingByKey } = this.getGraphIndex(run);
    const pausedNode = nodeByKey.get(run.pausedNodeKey);
    if (!pausedNode) {
      run.status = 'failed';
      run.error = { code: 'INVALID_RESUME_NODE', message: `Paused node missing` };
      return this.toRunState(run);
    }

    if (outcome.output) {
      run.payload = { ...run.payload, ...outcome.output };
    }

    const outgoing = outgoingByKey.get(pausedNode.nodeKey) ?? [];
    const resolution = this.resolveNextEdge(outgoing, outcome.edgeId, outcome.sourceHandle, run.payload);
    if (!resolution.ok) {
      run.status = 'failed';
      run.error = { code: resolution.code, message: resolution.message };
      return this.toRunState(run);
    }

    run.trace.push({
      nodeKey: pausedNode.nodeKey,
      nodeType: pausedNode.nodeType,
      category: this.registry.resolveCategory(pausedNode.nodeType),
      status: 'success',
      edgeIdTaken: resolution.edge.id,
    });
    run.pausedNodeKey = undefined;
    run.status = 'running';

    const next = nodeByKey.get(resolution.edge.targetNodeKey);
    if (!next) {
      run.status = 'failed';
      run.error = {
        code: 'INVALID_EDGE_TARGET',
        message: `Edge targets unknown node "${resolution.edge.targetNodeKey}"`,
      };
      return this.toRunState(run);
    }

    await this.executeFrom(run, next, DEFAULT_MAX_STEPS);
    this.persist(run);
    return this.toRunState(run);
  }

  async getState(instanceId: string): Promise<RunState | null> {
    const run = await this.resolveRun(instanceId);
    return run ? this.toRunState(run) : null;
  }

  /** Latest persisted instance id for a workflow key, when available. */
  async readActiveInstanceId(workflowKey: string): Promise<string | null> {
    try {
      return await this.runStorage.getActiveInstanceId(workflowKey);
    } catch {
      return null;
    }
  }

  /** List persisted instance ids for a workflow key (fallback hydration). */
  async listStoredInstanceIds(workflowKey: string): Promise<string[]> {
    try {
      return await this.runStorage.listInstanceIdsForWorkflow(workflowKey);
    } catch {
      return [];
    }
  }

  private async resolveRun(instanceId: string): Promise<InternalRun | null> {
    const cached = this.runs.get(instanceId);
    if (cached) {
      return cached;
    }

    const restored = await this.readStoredRun(instanceId);
    if (!restored) {
      return null;
    }

    await this.ensureDefinition(restored);
    this.runs.set(instanceId, restored);
    return restored;
  }

  private async ensureDefinition(run: InternalRun): Promise<void> {
    if (run.definition?.nodes?.length) {
      return;
    }
    run.definition = await this.pack.getWorkflow(run.workflowKey);
    run.graph = undefined;
  }

  private async executeFrom(
    run: InternalRun,
    start: CanonicalNode,
    maxSteps: number,
  ): Promise<void> {
    const { nodeByKey, outgoingByKey } = this.getGraphIndex(run);
    let current: CanonicalNode | undefined = start;
    let steps = 0;

    const ctxBase = (): ExecutorContext => ({
      workflowKey: run.definition.workflowKey,
      versionNumber: run.definition.versionNumber,
      payload: run.payload,
      proxy: this.proxy,
      expressions: this.expressions,
      pack: this.pack,
    });

    while (current) {
      if (steps++ >= maxSteps) {
        run.status = 'failed';
        run.error = {
          code: 'MAX_STEPS_EXCEEDED',
          message: `Execution exceeded ${maxSteps} steps`,
        };
        return;
      }

      // go-to jump support
      if (typeof run.payload['__gotoNodeKey'] === 'string') {
        const targetKey = String(run.payload['__gotoNodeKey']);
        delete run.payload['__gotoNodeKey'];
        const target = nodeByKey.get(targetKey);
        if (target) {
          current = target;
          continue;
        }
      }

      if (this.registry.isEndNode(current.nodeType)) {
        run.trace.push({
          nodeKey: current.nodeKey,
          nodeType: current.nodeType,
          category: 'flow-control',
          status: 'success',
        });
        run.status = 'completed';
        run.pausedNodeKey = undefined;
        return;
      }

      const outgoing = outgoingByKey.get(current.nodeKey) ?? [];

      if (this.registry.isStartNode(current.nodeType)) {
        run.trace.push({
          nodeKey: current.nodeKey,
          nodeType: current.nodeType,
          category: 'flow-control',
          status: 'success',
        });
        const resolution = this.resolveNextEdge(outgoing, undefined, undefined, run.payload);
        if (!resolution.ok) {
          run.status = 'failed';
          run.error = { code: resolution.code, message: resolution.message };
          return;
        }
        current = nodeByKey.get(resolution.edge.targetNodeKey);
        continue;
      }

      if (this.registry.isPauseNode(current.nodeType)) {
        run.status = 'paused';
        run.pausedNodeKey = current.nodeKey;
        run.trace.push({
          nodeKey: current.nodeKey,
          nodeType: current.nodeType,
          category: this.registry.resolveCategory(current.nodeType),
          status: 'paused',
        });
        return;
      }

      const category = this.registry.resolveCategory(current.nodeType);
      const executor = this.executors[category];
      if (!executor) {
        run.status = 'failed';
        run.error = {
          code: 'UNSUPPORTED_NODE_CATEGORY',
          message: `No executor for category "${category}"`,
        };
        return;
      }

      const result = await executor(current, ctxBase(), outgoing);
      if (result.status === 'pause') {
        run.status = 'paused';
        run.pausedNodeKey = current.nodeKey;
        run.trace.push({
          nodeKey: current.nodeKey,
          nodeType: current.nodeType,
          category,
          status: 'paused',
        });
        return;
      }
      if (result.status === 'failure') {
        run.status = 'failed';
        run.error = { code: result.code ?? 'NODE_FAILED', message: result.message };
        run.trace.push({
          nodeKey: current.nodeKey,
          nodeType: current.nodeType,
          category,
          status: 'failure',
          message: result.message,
        });
        return;
      }

      if (result.output) {
        run.payload = { ...run.payload, ...result.output };
      }

      // flow-control.go-to may set __gotoNodeKey
      if (typeof run.payload['__gotoNodeKey'] === 'string') {
        run.trace.push({
          nodeKey: current.nodeKey,
          nodeType: current.nodeType,
          category,
          status: 'success',
        });
        const target = nodeByKey.get(String(run.payload['__gotoNodeKey']));
        delete run.payload['__gotoNodeKey'];
        current = target;
        continue;
      }

      const resolution = this.resolveNextEdge(
        outgoing,
        result.nextEdgeId,
        result.nextSourceHandle,
        run.payload,
      );
      if (!resolution.ok) {
        // Terminal nodes without outgoing edges complete successfully when category says so
        if (outgoing.length === 0) {
          run.trace.push({
            nodeKey: current.nodeKey,
            nodeType: current.nodeType,
            category,
            status: 'success',
          });
          run.status = 'completed';
          return;
        }
        run.status = 'failed';
        run.error = { code: resolution.code, message: resolution.message };
        return;
      }

      run.trace.push({
        nodeKey: current.nodeKey,
        nodeType: current.nodeType,
        category,
        status: 'success',
        edgeIdTaken: resolution.edge.id,
      });
      current = nodeByKey.get(resolution.edge.targetNodeKey);
    }

    run.status = 'completed';
  }

  private getGraphIndex(run: InternalRun): WorkflowGraphIndex {
    if (!run.graph) {
      run.graph = this.buildGraph(run.definition);
    }
    return run.graph;
  }

  private buildGraph(definition: CanonicalWorkflowDocument): WorkflowGraphIndex {
    const nodeByKey = new Map(definition.nodes.map((n) => [n.nodeKey, n]));
    const outgoingByKey = new Map<string, CanonicalEdge[]>();
    for (const edge of definition.edges) {
      const list = outgoingByKey.get(edge.sourceNodeKey) ?? [];
      list.push(edge);
      outgoingByKey.set(edge.sourceNodeKey, list);
    }
    for (const list of outgoingByKey.values()) {
      list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
    return { nodeByKey, outgoingByKey };
  }

  private resolveNextEdge(
    outgoing: CanonicalEdge[],
    edgeId?: string,
    sourceHandle?: string,
    payload: WorkflowPayload = {},
  ):
    | { ok: true; edge: CanonicalEdge }
    | { ok: false; code: string; message: string } {
    if (edgeId) {
      const edge = outgoing.find((e) => e.id === edgeId);
      if (!edge) {
        return { ok: false, code: 'UNKNOWN_EDGE', message: `Edge "${edgeId}" not found` };
      }
      return { ok: true, edge };
    }
    if (sourceHandle) {
      const edge = outgoing.find((e) => e.sourceHandle === sourceHandle);
      if (edge) return { ok: true, edge };
    }

    // Condition-driven (choice / decision edges) — outgoing is pre-sorted by sortOrder.
    const matched = outgoing.find(
      (edge) =>
        !edge.isDefault &&
        edge.condition != null &&
        evaluateCondition(edge.condition as ConditionExpression, payload),
    );
    if (matched) return { ok: true, edge: matched };

    const def = outgoing.find((e) => e.isDefault);
    if (def) return { ok: true, edge: def };

    if (outgoing.length === 1) return { ok: true, edge: outgoing[0]! };
    if (outgoing.length === 0) {
      return { ok: false, code: 'NO_OUTGOING', message: 'Node has no outgoing edges' };
    }
    return {
      ok: false,
      code: 'AMBIGUOUS_EDGE',
      message: 'Multiple outgoing edges; specify edgeId or sourceHandle',
    };
  }

  private toRunState(run: InternalRun): RunState {
    const graph = this.getGraphIndex(run);
    const paused = run.pausedNodeKey ? graph.nodeByKey.get(run.pausedNodeKey) : undefined;
    const outgoing = paused ? graph.outgoingByKey.get(paused.nodeKey) ?? [] : [];

    const availableEdges: RunStateEdge[] = outgoing.map((e) => ({
      id: e.id,
      label: e.label,
      sourceHandle: e.sourceHandle,
      isDefault: e.isDefault,
      condition: normalizeCondition(e.condition),
    }));

    const state: RunState = {
      status: run.status,
      workflowKey: run.definition.workflowKey,
      versionNumber: run.definition.versionNumber,
      currentNodeType: paused?.nodeType ?? (run.status === 'completed' ? 'END' : run.status === 'failed' ? 'FAILED' : ''),
      currentNodeKey: paused?.nodeKey,
      availableEdges,
      contextSoFar: { ...run.payload },
      availableActions: [],
      instanceId: run.instanceId,
      error: run.error,
    };

    if (paused) {
      this.enrichPauseUi(state, paused);
    }
    return state;
  }

  private enrichPauseUi(state: RunState, node: CanonicalNode): void {
    const config = node.configuration ?? {};
    const type = node.nodeType.toLowerCase();

    if (this.registry.isDatasetNode(type)) {
      state.datasetNodeKey = node.nodeKey;
      state.datasetUiConfig = this.proxy.sanitizeDataSourceConfig(config);
      state.availableActions = ['row-click', 'action-click', 'refresh'];
    } else if (this.registry.isRecordDetailNode(type)) {
      state.recordDetailNodeKey = node.nodeKey;
      state.recordDetailUiConfig = this.proxy.sanitizeDataSourceConfig(config);
      state.availableActions = ['action-click', 'back'];
    } else if (this.registry.isFormEntryNode(type)) {
      state.formId = typeof config['formId'] === 'string' ? config['formId'] : undefined;
      state.formVersionId =
        typeof config['formVersionId'] === 'string' ? config['formVersionId'] : undefined;
      state.formUiConfig = {
        mode: typeof config['mode'] === 'string' ? config['mode'] : 'create',
        formId: state.formId,
        formVersionId: state.formVersionId,
      };
      state.availableActions = ['submit', 'cancel'];
    } else if (this.registry.isChoiceNode(type)) {
      const optionsRaw = config['applicantTypes'] ?? config['statusOptions'] ?? [];
      const options = Array.isArray(optionsRaw)
        ? optionsRaw.map(String)
        : [];
      const fieldCode = type.includes('applicant-type') ? 'applicantTypes' : 'customerStatus';
      state.choiceUiConfig = {
        prompt: node.label,
        fieldCode,
        options,
        continueLabel: 'Continue',
      };
      state.availableActions = ['choose'];
    } else if (this.registry.isOtpNode(type)) {
      state.otpUiConfig = {
        field: typeof config['field'] === 'string' ? config['field'] : undefined,
        provider: typeof config['provider'] === 'string' ? config['provider'] : undefined,
        timeoutSeconds:
          typeof config['timeoutSeconds'] === 'number' ? config['timeoutSeconds'] : 120,
      };
      state.availableActions = ['verify-otp'];
    } else if (this.registry.isDocumentUploadNode(type)) {
      state.documentUploadUiConfig = {
        documentType: typeof config['documentType'] === 'string' ? config['documentType'] : undefined,
        maxSizeMB: typeof config['maxSizeMB'] === 'number' ? config['maxSizeMB'] : 10,
        allowedFormats: config['allowedFormats'],
      };
      state.availableActions = ['upload'];
    } else if (
      this.registry.isApprovalCategory(type) ||
      this.registry.resolveCategory(type) === 'decision'
    ) {
      state.approvalUiConfig = {
        label: typeof config['label'] === 'string' ? config['label'] : node.label,
        outcomeValue:
          typeof config['outcomeValue'] === 'string' ? config['outcomeValue'] : undefined,
        instructions:
          typeof config['instructions'] === 'string' ? config['instructions'] : undefined,
      };
      state.availableActions = ['approve', 'reject'];
    } else if (this.registry.isTaskPauseNode(type) || this.registry.resolveCategory(type) === 'tasks') {
      state.taskUiConfig = {
        assignTo: typeof config['assignTo'] === 'string' ? config['assignTo'] : undefined,
        instructions:
          typeof config['instructions'] === 'string' ? config['instructions'] : undefined,
        dueInHours: typeof config['dueInHours'] === 'number' ? config['dueInHours'] : undefined,
        priority: typeof config['priority'] === 'string' ? config['priority'] : undefined,
      };
      state.availableActions = ['complete'];
    }
  }

  private persist(run: InternalRun): void {
    try {
      const snapshot: WorkflowRunStorageSnapshot = {
        instanceId: run.instanceId,
        workflowKey: run.workflowKey,
        versionNumber: run.versionNumber,
        payload: run.payload,
        trace: run.trace,
        pausedNodeKey: run.pausedNodeKey,
        status: run.status,
        error: run.error,
      };
      this.runStorage.saveRun(snapshot);
    } catch {
      // ignore storage errors
    }
  }

  private async readStoredRun(instanceId: string): Promise<InternalRun | null> {
    try {
      const parsed = await this.runStorage.getRun(instanceId);
      if (!parsed?.workflowKey || parsed.versionNumber == null) {
        return null;
      }
      return {
        instanceId: parsed.instanceId,
        workflowKey: parsed.workflowKey,
        versionNumber: parsed.versionNumber,
        definition: null as unknown as CanonicalWorkflowDocument,
        payload: parsed.payload ?? {},
        trace: parsed.trace ?? [],
        pausedNodeKey: parsed.pausedNodeKey,
        status: parsed.status ?? 'running',
        error: parsed.error,
      };
    } catch {
      return null;
    }
  }
}

function normalizeCondition(
  condition: unknown,
): RunStateEdge['condition'] | undefined {
  if (!condition || typeof condition !== 'object') return undefined;
  const c = condition as Record<string, unknown>;
  if (typeof c['op'] === 'string' && typeof c['fieldCode'] === 'string') {
    return { op: c['op'], fieldCode: c['fieldCode'], value: c['value'] };
  }
  return undefined;
}
